import { Dialog, DialogTitle, DialogContent, Box, Typography, Stack, Chip, Divider } from '@mui/material';
import { Command, FileText, Search, Keyboard } from 'lucide-react';

interface KeyboardShortcutsModalProps {
  open: boolean;
  onClose: () => void;
}

interface Shortcut {
  keys: string[];
  description: string;
  category: string;
}

const shortcuts: Shortcut[] = [
  { keys: ['Ctrl', 'K'], description: 'Global search', category: 'Navigation' },
  { keys: ['Ctrl', 'N'], description: 'New appointment', category: 'Actions' },
  { keys: ['Ctrl', 'P'], description: 'Search patients', category: 'Actions' },
  { keys: ['Ctrl', 'B'], description: 'Create bill', category: 'Actions' },
  { keys: ['Ctrl', 'S'], description: 'Save/Submit form', category: 'Forms' },
  { keys: ['Esc'], description: 'Close modal/Cancel', category: 'Forms' },
  { keys: ['Tab'], description: 'Next field', category: 'Forms' },
  { keys: ['Shift', 'Tab'], description: 'Previous field', category: 'Forms' },
  { keys: ['?'], description: 'Show this help', category: 'Help' },
];

const categoryIcons: Record<string, typeof Command> = {
  'Navigation': Search,
  'Actions': Command,
  'Forms': Keyboard,
  'Help': FileText,
};

export function KeyboardShortcutsModal({ open, onClose }: KeyboardShortcutsModalProps) {
  const categories = Array.from(new Set(shortcuts.map(s => s.category)));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Keyboard size={24} />
        <Typography variant="h6" fontWeight={700}>
          Keyboard Shortcuts
        </Typography>
      </DialogTitle>
      
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Speed up your workflow with these keyboard shortcuts
        </Typography>

        {categories.map((category, index) => {
          const Icon = categoryIcons[category] || Command;
          const categoryShortcuts = shortcuts.filter(s => s.category === category);
          
          return (
            <Box key={category} sx={{ mb: 3 }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                <Icon size={18} />
                <Typography variant="subtitle2" fontWeight={700}>
                  {category}
                </Typography>
              </Stack>
              
              <Stack spacing={1.5}>
                {categoryShortcuts.map((shortcut, idx) => (
                  <Stack
                    key={idx}
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{
                      p: 1.5,
                      borderRadius: 1,
                      bgcolor: 'action.hover',
                    }}
                  >
                    <Typography variant="body2">{shortcut.description}</Typography>
                    <Stack direction="row" spacing={0.5}>
                      {shortcut.keys.map((key, keyIdx) => (
                        <Box key={keyIdx} sx={{ display: 'flex', alignItems: 'center' }}>
                          <Chip
                            label={key}
                            size="small"
                            sx={{
                              height: 24,
                              fontSize: '0.75rem',
                              fontWeight: 600,
                              bgcolor: 'background.paper',
                              border: '1px solid',
                              borderColor: 'divider',
                            }}
                          />
                          {keyIdx < shortcut.keys.length - 1 && (
                            <Typography variant="caption" sx={{ mx: 0.5 }}>
                              +
                            </Typography>
                          )}
                        </Box>
                      ))}
                    </Stack>
                  </Stack>
                ))}
              </Stack>
              
              {index < categories.length - 1 && <Divider sx={{ mt: 3 }} />}
            </Box>
          );
        })}

        <Box
          sx={{
            mt: 3,
            p: 2,
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            borderRadius: 2,
          }}
        >
          <Typography variant="caption" fontWeight={600}>
            💡 Tip: Press <strong>?</strong> anytime to show this help dialog
          </Typography>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
